import Button from '../../button/Button';
import { Product, ProductVariantSelectedOption } from '../../../models/Product';
import './ProductVariantPicker.less';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { useTranslation } from '../../../hooks/useTranslation';

export type ProductVariantPickerProps = {
    product: Product;
    isLoading?: boolean;
    showSingleVariant: boolean;
    onChange: (productId: string, variantId: string | undefined) => void;
};

const ProductVariantPicker = ({
    product,
    isLoading = false,
    showSingleVariant,
    onChange
}: ProductVariantPickerProps) => {
    const { t } = useTranslation();
    const isOptionDisabled = (
        selected: ProductVariantSelectedOption[],
        name: string,
        value: string
    ) => {
        const otherSelected = selected.filter(
            so => so.value !== '' && so.name !== name
        );
        return !product.variants.some(
            v =>
                v.available &&
                v.selectedOptions.some(
                    so => so.name === name && so.value === value
                ) &&
                otherSelected.every(s =>
                    v.selectedOptions.some(
                        so => so.name === s.name && so.value === s.value
                    )
                )
        );
    };
    const [selectedOptions, setSelectedOptions] = useState(
        product.options.map<ProductVariantSelectedOption>(option => ({
            name: option.name,
            value:
                option.values.find(
                    v => !isOptionDisabled([], option.name, v)
                ) ?? ''
        }))
    );
    const onClickValue = useCallback((name: string, value: string) => {
        setSelectedOptions(selectedOptions =>
            selectedOptions.map(so => (so.name === name ? { name, value } : so))
        );
    }, []);
    const getSelectedValue = (name: string) => {
        return selectedOptions.find(so => so.name === name)?.value;
    };
    const isValueSelected = (name: string, value: string) => {
        return selectedOptions.some(
            so => so.name === name && so.value === value
        );
    };
    const selectedVariant =
        product.variants.length === 1
            ? product.variants[0]
            : product.variants.find(v =>
                  selectedOptions.every(o =>
                      v.selectedOptions.some(
                          so => so.name === o.name && so.value === o.value
                      )
                  )
              );
    useEffect(() => {
        onChange(product.productId, selectedVariant?.variantId);
    }, [product.productId, selectedVariant?.variantId, onChange]);

    if (!showSingleVariant && product.variants.length === 1 && !isLoading) {
        return null;
    }

    return (
        <div className={`bri-ProductVariantPicker`}>
            {!showSingleVariant && product.variants.length === 1 && (
                <div className="bri-ProductVariantPicker-options">
                    <div className="bri-BodyFont">
                        {t('ADDING_TO_CART_SINGLE_VARIANT')}
                    </div>
                </div>
            )}
            {product.options.length > 0 && (
                <div className="bri-ProductVariantPicker-options">
                    {product.options.map(option => (
                        <div className="bri-ProductVariantPicker-option">
                            <div className="bri-BodyFont">
                                {option.name}:&nbsp;
                                <span className="bri-BodyBoldFont">
                                    {getSelectedValue(option.name)}
                                </span>
                            </div>
                            <div className="bri-ProductVariantPicker-values">
                                {option.values.map(value => (
                                    <Button
                                        key={value}
                                        className={`bri-ProductVariantPicker-value${
                                            isValueSelected(option.name, value)
                                                ? ' bri-selected'
                                                : ''
                                        }`}
                                        variant={'secondary'}
                                        size="medium"
                                        onClick={() =>
                                            onClickValue(option.name, value)
                                        }
                                        disabled={
                                            isLoading ||
                                            isOptionDisabled(
                                                selectedOptions,
                                                option.name,
                                                value
                                            )
                                        }
                                    >
                                        {value}
                                    </Button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProductVariantPicker;
